import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { colors } from "../../../theme/colors";

interface StationStatusConfirmDialogProps {
  open: boolean;
  isActive: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const StationStatusConfirmDialog: React.FC<StationStatusConfirmDialogProps> = ({
  open,
  isActive,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const isDeactivating = isActive;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            border: `1.5px solid ${isDeactivating ? "#D32F2F" : "#43A047"}`,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 1,
          pt: 3,
          px: 4,
        }}
      >
        {isDeactivating ? (
          <WarningIcon sx={{ color: "#D32F2F", fontSize: 25 }} />
        ) : (
          <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 25 }} />
        )}
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "20px",
            fontFamily: "'Inter', sans-serif ",
            color: isDeactivating ? "#D32F2F" : "#2E7D32",
          }}
        >
          {isDeactivating ? "Confirm Deactivation" : "Confirm Activation"}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 4, pt: 1.5, pb: 2 }}>
        <Typography
          sx={{
            color: colors.textGray,
            fontSize: "16px",
            fontWeight: 400,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {isDeactivating
            ? "Are you sure you want to deactivate this station? It will be temporarily disabled, and all data will be retained."
            : "Are you sure you want to activate this station? It will be temporarily enabled, and all data will be retained."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 3, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            flex: 1,
            textTransform: "none",
            color: "#616161",
            borderColor: "#D0D5DD",
            py: 1.2,
            fontSize: "14px",
            fontFamily: "'Inter', sans-serif",
            "&:hover": {
              borderColor: "#B0B7C3",
              bgcolor: "#FAFAFA",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : undefined
          }
          sx={{
            flex: 1,
            textTransform: "none",
            py: 1.2,
            fontSize: "14px",
            fontFamily: "'Inter', sans-serif",
            boxShadow: 2,
            bgcolor: isDeactivating ? "#D32F2F" : "#2E7D32",
            "&:hover": {
              bgcolor: isDeactivating ? "#D32F2F" : "#2E7D32",
            },
          }}
        >
          {isSubmitting
            ? "Please wait..."
            : isDeactivating
              ? "Deactivate"
              : "Activate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StationStatusConfirmDialog;
